import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { openExternal } from "@/lib/nativeBridge";
import { toast } from "sonner";
import {
  CheckCircleIcon as CheckCircle,
  ArrowSquareOutIcon as ArrowSquareOut,
  ArrowsClockwiseIcon as ArrowsClockwise,
  SpinnerIcon as Spinner,
} from "@phosphor-icons/react";

/**
 * Action-first Stripe Tax setup checklist. Three steps, each with a
 * button that DOES the thing — no reading required. Lives at the top
 * of /help/stripe-tax-setup; the long-form explainer stays below for
 * sellers who want the details.
 */
const StepDot = ({ done, n }) =>
  done ? (
    <CheckCircle size={30} weight="fill" className="text-emerald-600 flex-shrink-0" />
  ) : (
    <span className="inline-flex h-[30px] w-[30px] items-center justify-center rounded-full bg-gold-gradient text-ink font-bold text-[14px] flex-shrink-0">
      {n}
    </span>
  );

export const StripeTaxQuickSetup = () => {
  const [status, setStatus] = useState(null);
  const [opening, setOpening] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    api.get("/me/tax-status").then((r) => setStatus(r.data)).catch(() => setStatus({ connected: false }));
  }, []);

  const openDashboard = async () => {
    setOpening(true);
    try {
      const { data } = await api.post("/connect/dashboard-link");
      openExternal(data.url);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "object" ? d?.message : d;
      toast.error(msg || "Couldn't open your Stripe dashboard — try again in a minute.");
      if (typeof d === "object" && d?.stripe_direct_login_url) openExternal(d.stripe_direct_login_url);
    } finally {
      setOpening(false);
    }
  };

  const checkStatus = async () => {
    setChecking(true);
    try {
      const { data } = await api.get("/me/tax-status", { params: { refresh: 1 } });
      setStatus(data);
      if (data.tax_configured) toast.success("Stripe Tax is on — you're all set!");
      else toast.info(data.reason || "Not detected yet — finish the steps in Stripe, then check again.");
    } catch {
      toast.error("Couldn't check right now — try again in a minute.");
    } finally {
      setChecking(false);
    }
  };

  if (!status) return null;

  const payoutsReady = status.connected && status.payouts_enabled;
  const sellerState = status.seller_state;
  const isCA = !sellerState || sellerState.code === "CA";

  if (status.tax_configured) {
    return (
      <div
        className="mb-10 rounded-xl border border-emerald-300 bg-emerald-50 p-5 flex items-start gap-3"
        data-testid="tax-quick-setup-done"
      >
        <CheckCircle size={26} weight="fill" className="text-emerald-600 flex-shrink-0" />
        <div>
          <p className="font-bold text-emerald-900 text-[15px]">Stripe Tax is on — you're done</p>
          <p className="text-[13px] text-emerald-900/80 mt-1 leading-relaxed">
            Every sale now collects the right sales tax automatically. One thing stays yours:
            filing and remitting to your state each period — Stripe collects, you file.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="mb-10 rounded-xl border-2 border-gold/60 bg-[#FBF7EC] p-6 shadow-gold-glow"
      data-testid="tax-quick-setup"
    >
      <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold-dark mb-1">Do it now</p>
      <h2 className="font-spectral text-2xl text-ink tracking-tight mb-5">3 steps, one-time setup</h2>
      <ol className="space-y-5">
        <li className="flex items-start gap-4" data-testid="tax-quick-step-1">
          <StepDot done={payoutsReady} n={1} />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-ink text-[14.5px]">Stripe payouts working</p>
            {payoutsReady ? (
              <p className="text-[13px] text-emerald-700 mt-0.5">Done — your Stripe account is connected and payouts are on.</p>
            ) : (
              <>
                <p className="text-[13px] text-[#3F3F3F] mt-0.5">Connect Stripe and finish payouts first — everything else builds on it.</p>
                <Link
                  to="/payouts"
                  className="mt-2 inline-flex items-center gap-1.5 bg-ink hover:bg-crimson text-white rounded-full px-4 py-2 text-[12.5px] font-semibold transition-colors"
                  data-testid="tax-quick-step-1-cta"
                >
                  Finish payouts setup
                </Link>
              </>
            )}
          </div>
        </li>
        <li className="flex items-start gap-4" data-testid="tax-quick-step-2">
          <StepDot done={false} n={2} />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-ink text-[14.5px]">Turn on Tax in your Stripe dashboard</p>
            <p className="text-[13px] text-[#3F3F3F] mt-0.5 leading-relaxed">
              Once it opens: tap <strong>Tax</strong> in the menu → <strong>Activate</strong> → confirm your
              business address → under <strong>Registrations</strong>, add{" "}
              <strong>{sellerState ? sellerState.name : "the state you sell from"}</strong> with your
              seller's permit number{isCA ? " (in California that's your CDTFA permit)" : ""}.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-2.5">
              <button
                onClick={openDashboard}
                disabled={opening || !payoutsReady}
                className="inline-flex items-center gap-1.5 bg-ink hover:bg-crimson disabled:opacity-50 text-white rounded-full px-4 py-2 text-[12.5px] font-semibold transition-colors"
                data-testid="tax-quick-step-2-cta"
              >
                {opening ? <Spinner size={13} className="animate-spin" /> : <ArrowSquareOut size={13} weight="bold" />}
                Open my Stripe dashboard
              </button>
              {isCA ? (
                <a
                  href="https://onlineservices.cdtfa.ca.gov/_/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[12px] text-crimson underline underline-offset-2"
                  data-testid="tax-quick-permit-link"
                >
                  No CA seller's permit yet? Get one free (~20 min)
                </a>
              ) : (
                <a
                  href="https://www.irs.gov/businesses/small-businesses-self-employed/state-government-websites"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[12px] text-crimson underline underline-offset-2"
                  data-testid="tax-quick-permit-link"
                >
                  No permit yet? Find {sellerState.name}'s tax office
                </a>
              )}
            </div>
            <p className="text-[12px] text-[#6B6B6B] mt-2 leading-relaxed" data-testid="tax-quick-step-2-note">
              {sellerState
                ? `We read ${sellerState.name} from your kennel address — if you actually sell from a different state, just pick that state in Stripe instead.`
                : "Register with the tax office of the state you sell from, then add that state in Stripe."}
            </p>
          </div>
        </li>
        <li className="flex items-start gap-4" data-testid="tax-quick-step-3">
          <StepDot done={false} n={3} />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-ink text-[14.5px]">Confirm it worked</p>
            <p className="text-[13px] text-[#3F3F3F] mt-0.5">Come back here and tap the button — we'll verify with Stripe.</p>
            <button
              onClick={checkStatus}
              disabled={checking}
              className="mt-2 inline-flex items-center gap-1.5 border-2 border-ink hover:bg-ink hover:text-white disabled:opacity-50 text-ink rounded-full px-4 py-2 text-[12.5px] font-semibold transition-colors"
              data-testid="tax-quick-step-3-cta"
            >
              {checking ? <Spinner size={13} className="animate-spin" /> : <ArrowsClockwise size={13} weight="bold" />}
              Check my setup
            </button>
          </div>
        </li>
      </ol>
      <p className="text-[12px] text-[#6B6B6B] mt-5 pt-4 border-t border-[#E2DAC2] leading-relaxed" data-testid="tax-quick-optional-note">
        <strong>Prefer to skip this?</strong> That's fine — Stripe Tax is optional. Sales still go
        through and you still get paid; your receipts just won't itemise tax, so you'd work it out
        yourself at filing time. You can come back and set it up whenever you like.
      </p>
      <p className="text-[12px] text-[#8A6A00] mt-3 leading-relaxed" data-testid="tax-quick-responsibility-note">
        <strong>Your responsibility, not ours:</strong> you are the seller of record. Registering,
        collecting, and filing sales tax is yours to handle — Armstrong K9 Registry is a venue and
        never collects, files, or remits tax on your behalf. This page is guidance, not tax advice.
      </p>
    </div>
  );
};

export default StripeTaxQuickSetup;
